/**
 * ============================================================================
 * ACTIVITY SEARCH SERVICE - Phase 2
 * ============================================================================
 * Advanced search capabilities for activity tracking
 *
 * @pattern Phase 4 - IService + ServiceResponse + typedSupabase
 * @version 2.0.0
 * @date 2026-02-20
 */

import { typedSupabase } from "../lib/typed-supabase";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError } from "./service-registry";
import { logger } from "../lib/logger";

// ============================================================================
// TYPES
// ============================================================================

export interface ActivitySearchResult {
  id: string;
  domain: string;
  entity_id: string;
  user_id: string;
  username: string;
  full_name: string;
  action_type: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
  relevance: number;
}

export interface SearchActivitiesParams {
  searchQuery: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  actionTypes?: string[];
  domains?: ('task' | 'sales' | 'organization')[];
  limit?: number;
  offset?: number;
}

export interface SavedSearch {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  search_params: SearchActivitiesParams;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSavedSearchParams {
  name: string;
  description?: string;
  searchParams: SearchActivitiesParams;
  isPublic?: boolean;
}

export interface SearchSuggestion {
  suggestion_type: string;
  suggestion_value: string;
  usage_count: number;
}

interface DbActivitySearchResult {
  id: string;
  domain: string;
  entity_id: string;
  user_id: string;
  username: string | null;
  full_name: string | null;
  action_type: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
  relevance: number | string | null;
}

interface DbSearchSuggestion {
  suggestion_type: string;
  suggestion_value: string;
  usage_count: number | string;
}

// ============================================================================
// SERVICE INTERFACE
// ============================================================================

export interface IActivitySearchService extends IService {
  /**
   * Search across all activity types with full-text search
   * @param params - Search parameters including query, filters, and pagination
   * @returns Ranked list of matching activities
   */
  searchActivities(params: SearchActivitiesParams): Promise<ServiceResponse<ActivitySearchResult[]>>;

  /**
   * Get search suggestions based on user's activity history
   * @param userId - User ID to get suggestions for
   * @param limit - Maximum number of suggestions
   * @returns List of suggested search terms
   */
  getSearchSuggestions(userId: string, limit?: number): Promise<ServiceResponse<SearchSuggestion[]>>;

  /**
   * Save a search preset for future use
   * @param params - Search preset details
   * @returns Created saved search
   */
  createSavedSearch(params: CreateSavedSearchParams): Promise<ServiceResponse<SavedSearch>>;

  /**
   * Get user's saved searches
   * @param userId - User ID to fetch saved searches for
   * @returns List of saved searches
   */
  getSavedSearches(userId: string): Promise<ServiceResponse<SavedSearch[]>>;

  /**
   * Get public saved searches
   * @param limit - Maximum number of results
   * @returns List of public saved searches
   */
  getPublicSavedSearches(limit?: number): Promise<ServiceResponse<SavedSearch[]>>;

  /**
   * Delete a saved search
   * @param searchId - ID of the saved search to delete
   * @returns Success response
   */
  deleteSavedSearch(searchId: string): Promise<ServiceResponse<void>>;

  /**
   * Update a saved search
   * @param searchId - ID of the saved search to update
   * @param updates - Fields to update
   * @returns Updated saved search
   */
  updateSavedSearch(
    searchId: string,
    updates: Partial<CreateSavedSearchParams>
  ): Promise<ServiceResponse<SavedSearch>>;
}

// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================

export class ActivitySearchService implements IActivitySearchService {
  readonly name = "ActivitySearchService";

  async initialize(): Promise<void> {
    logger.info("✅ ActivitySearchService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("🧹 ActivitySearchService disposed");
  }

  async searchActivities(params: SearchActivitiesParams): Promise<ServiceResponse<ActivitySearchResult[]>> {
    const {
      searchQuery,
      userId,
      startDate,
      endDate,
      actionTypes,
      domains,
      limit = 50,
      offset = 0,
    } = params;

    logger.debug("Searching activities", { searchQuery, userId, limit, offset });

    try {
      const { data, error } = await typedSupabase.rpc(
        "search_activities",
        {
          p_search_query: searchQuery || '',
          p_user_id: userId || null,
          p_start_date: startDate || null,
          p_end_date: endDate || null,
          p_action_types: actionTypes || null,
          p_domains: domains || null,
          p_limit: limit,
          p_offset: offset,
        }
      );

      if (error) {
        return { success: false, error: error.message, code: "SEARCH_FAILED", service: this.name, context: { params, error } };
      }

      const results = (data as unknown as DbActivitySearchResult[])?.map(item => ({
        id: item.id,
        domain: item.domain,
        entity_id: item.entity_id,
        user_id: item.user_id,
        username: item.username || '',
        full_name: item.full_name || '',
        action_type: item.action_type,
        field_name: item.field_name,
        old_value: item.old_value,
        new_value: item.new_value,
        details: item.details,
        created_at: item.created_at,
        relevance: Number(item.relevance) || 0,
      })) || [];

      return { success: true, data: results };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "SEARCH_FAILED", service: this.name, context: { params, error } };
    }
  }

  async getSearchSuggestions(userId: string, limit: number = 10): Promise<ServiceResponse<SearchSuggestion[]>> {
    logger.debug("Fetching search suggestions", { userId, limit });

    try {
      const { data, error } = await typedSupabase.rpc(
        "get_activity_search_suggestions",
        {
          p_user_id: userId,
          p_limit: limit,
        }
      );

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
      }

      const suggestions = (data as unknown as DbSearchSuggestion[])?.map(item => ({
        suggestion_type: item.suggestion_type,
        suggestion_value: item.suggestion_value,
        usage_count: Number(item.usage_count) || 0,
      })) || [];

      return { success: true, data: suggestions };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
    }
  }

  async createSavedSearch(params: CreateSavedSearchParams): Promise<ServiceResponse<SavedSearch>> {
    const { name, description, searchParams, isPublic = false } = params;

    logger.debug("Creating saved search", { name, isPublic });

    try {
      const { data, error } = await typedSupabase
        .from("saved_activity_searches" as any)
        .insert({
          name,
          description: description || null,
          search_params: searchParams as unknown as Record<string, unknown>,
          is_public: isPublic,
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
      }

      return { success: true, data: data as SavedSearch };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "CREATE_FAILED", service: this.name, context: { params, error } };
    }
  }

  async getSavedSearches(userId: string): Promise<ServiceResponse<SavedSearch[]>> {
    logger.debug("Fetching saved searches", { userId });

    try {
      const { data, error } = await typedSupabase
        .from("saved_activity_searches" as any)
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
      }

      return { success: true, data: (data as SavedSearch[]) || [] };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { userId, error } };
    }
  }

  async getPublicSavedSearches(limit: number = 20): Promise<ServiceResponse<SavedSearch[]>> {
    logger.debug("Fetching public saved searches", { limit });

    try {
      const { data, error } = await typedSupabase
        .from("saved_activity_searches" as any)
        .select("*")
        .eq("is_public", true)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { error } };
      }

      return { success: true, data: (data as SavedSearch[]) || [] };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { error } };
    }
  }

  async deleteSavedSearch(searchId: string): Promise<ServiceResponse<void>> {
    logger.debug("Deleting saved search", { searchId });

    try {
      const { error } = await typedSupabase
        .from("saved_activity_searches" as any)
        .delete()
        .eq("id", searchId);

      if (error) {
        return { success: false, error: error.message, code: "DELETE_FAILED", service: this.name, context: { searchId, error } };
      }

      return { success: true, data: undefined };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "DELETE_FAILED", service: this.name, context: { searchId, error } };
    }
  }

  async updateSavedSearch(
    searchId: string,
    updates: Partial<CreateSavedSearchParams>
  ): Promise<ServiceResponse<SavedSearch>> {
    logger.debug("Updating saved search", { searchId, updates });

    try {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.searchParams !== undefined) updateData.search_params = updates.searchParams;
      if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic;

      const { data, error } = await typedSupabase
        .from("saved_activity_searches" as any)
        .update(updateData)
        .eq("id", searchId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message, code: "UPDATE_FAILED", service: this.name, context: { searchId, updates, error } };
      }

      return { success: true, data: data as SavedSearch };
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: serviceError.message, code: "UPDATE_FAILED", service: this.name, context: { searchId, updates, error } };
    }
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const activitySearchService = new ActivitySearchService();
